import React, { useState, useEffect } from 'react';
import { X, Plus, Play, Save, Trash2, Sparkles, Filter, Pencil } from 'lucide-react';
import { smartPlaylistsApi } from '../api/smartPlaylists';
import { toast } from 'sonner';

const ENERGIA_OPTIONS = ['Muy Alta', 'Alta', 'Media', 'Baja'];
const MOMENTOS_OPTIONS = [
  'Primera Baile',
  'Baile General',
  'Hora Loca',
  'Cierre',
  'Cóctel',
  'Ceremonia',
  'General'
];

const SmartPlaylists = ({ genres = [], onClose, onApplyToCustomList }) => {
  const [savedPlaylists, setSavedPlaylists] = useState([]);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [generatedSongs, setGeneratedSongs] = useState([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [editingPlaylistId, setEditingPlaylistId] = useState(null); // Track which playlist is being edited
  
  // Ensure genres is an array
  const genresList = Array.isArray(genres) ? genres : [];
  
  // Criteria state
  const [criteria, setCriteria] = useState({
    name: '',
    genres: [],
    min_bpm: '',
    max_bpm: '',
    energia: [],
    momentos: [],
    favoritos_only: false,
    min_rating: '',
    limit: 50
  });

  // Load saved playlists on mount
  useEffect(() => {
    loadSavedPlaylists();
  }, []);

  const loadSavedPlaylists = async () => {
    try {
      const response = await smartPlaylistsApi.getAll();
      // API returns { playlists: [...] }
      const playlists = response?.playlists || response || [];
      setSavedPlaylists(Array.isArray(playlists) ? playlists : []);
    } catch (error) {
      console.error('Error loading smart playlists:', error);
      toast.error('Error al cargar playlists inteligentes');
      setSavedPlaylists([]);
    }
  };

  const handleGenerate = async () => {
    if (!criteria.name.trim()) {
      toast.error('Por favor ingresa un nombre para la playlist');
      return;
    }

    setIsGenerating(true);
    try {
      // Clean up criteria - convert empty strings to null
      const cleanCriteria = {
        ...criteria,
        min_bpm: criteria.min_bpm ? parseInt(criteria.min_bpm) : null,
        max_bpm: criteria.max_bpm ? parseInt(criteria.max_bpm) : null,
        min_rating: criteria.min_rating ? parseInt(criteria.min_rating) : null,
        limit: parseInt(criteria.limit) || 50
      };

      const result = await smartPlaylistsApi.generate(cleanCriteria);
      setGeneratedSongs(Array.isArray(result.songs) ? result.songs : []);
      toast.success(`Playlist generada: ${result.total || 0} canciones encontradas`);
    } catch (error) {
      console.error('Error generating smart playlist:', error);
      toast.error('Error al generar playlist inteligente');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleSave = async () => {
    if (!criteria.name.trim()) {
      toast.error('Por favor ingresa un nombre para la playlist');
      return;
    }

    setIsSaving(true);
    try {
      // Clean up criteria
      const cleanCriteria = {
        ...criteria,
        min_bpm: criteria.min_bpm ? parseInt(criteria.min_bpm) : null,
        max_bpm: criteria.max_bpm ? parseInt(criteria.max_bpm) : null,
        min_rating: criteria.min_rating ? parseInt(criteria.min_rating) : null,
        limit: parseInt(criteria.limit) || 50
      };

      if (editingPlaylistId) {
        // Update existing playlist
        await smartPlaylistsApi.update(editingPlaylistId, cleanCriteria);
        toast.success('Playlist inteligente actualizada');
      } else {
        // Create new playlist
        await smartPlaylistsApi.save(cleanCriteria);
        toast.success('Playlist inteligente guardada');
      }
      
      await loadSavedPlaylists();
      resetForm();
      setShowCreateDialog(false);
      setEditingPlaylistId(null);
    } catch (error) {
      console.error('Error saving smart playlist:', error);
      toast.error('Error al guardar playlist inteligente');
    } finally {
      setIsSaving(false);
    }
  };

  const handleLoadPlaylist = async (playlist) => {
    // Check if playlist has criteria
    if (!playlist.criteria) {
      toast.error('Esta playlist tiene formato antiguo. Por favor crea una nueva.');
      return;
    }
    
    // Ensure criteria arrays exist
    const safeCriteria = {
      ...playlist.criteria,
      genres: Array.isArray(playlist.criteria.genres) ? playlist.criteria.genres : [],
      energia: Array.isArray(playlist.criteria.energia) ? playlist.criteria.energia : [],
      momentos: Array.isArray(playlist.criteria.momentos) ? playlist.criteria.momentos : []
    };
    
    setCriteria(safeCriteria);
    setEditingPlaylistId(playlist.id); // Set the ID for editing mode
    setShowCreateDialog(true);
    // Auto-generate when loading
    setIsGenerating(true);
    try {
      const result = await smartPlaylistsApi.generate(safeCriteria);
      setGeneratedSongs(Array.isArray(result.songs) ? result.songs : []);
    } catch (error) {
      console.error('Error generating playlist:', error);
      setGeneratedSongs([]);
    } finally {
      setIsGenerating(false);
    }
  };
  
  const handleApplyToCustomList = async (playlist) => {
    try {
      // Check if playlist has criteria
      if (!playlist.criteria) {
        toast.error('Esta playlist tiene formato antiguo. Por favor crea una nueva.');
        return;
      }
      
      // Generar la playlist con los criterios guardados
      toast.info('Generando playlist inteligente...');
      const result = await smartPlaylistsApi.generate(playlist.criteria);
      
      if (result.songs && result.songs.length > 0) {
        // Llamar al callback para aplicar las canciones a la lista personalizada
        if (onApplyToCustomList) {
          onApplyToCustomList(result.songs);
          toast.success(`✅ ${result.songs.length} canciones aplicadas a la lista personalizada`);
          onClose();
        }
      } else {
        toast.warning('No se encontraron canciones que cumplan los criterios');
      }
    } catch (error) {
      console.error('Error applying smart playlist:', error);
      toast.error('Error al aplicar playlist inteligente');
    }
  };

  const handleDelete = async (playlistId) => {
    if (!window.confirm('¿Estás seguro de eliminar esta playlist inteligente?')) {
      return;
    }

    try {
      await smartPlaylistsApi.delete(playlistId);
      toast.success('Playlist inteligente eliminada');
      await loadSavedPlaylists();
    } catch (error) {
      console.error('Error deleting smart playlist:', error);
      toast.error(error.response?.data?.detail || 'Error al eliminar playlist inteligente');
    }
  };

  const resetForm = () => {
    setCriteria({
      name: '',
      genres: [],
      min_bpm: '',
      max_bpm: '',
      energia: [],
      momentos: [],
      favoritos_only: false,
      min_rating: '',
      limit: 50
    });
    setGeneratedSongs([]);
    setEditingPlaylistId(null); // Clear editing mode
  };

  const toggleGenre = (genre) => {
    setCriteria(prev => ({
      ...prev,
      genres: prev.genres.includes(genre)
        ? prev.genres.filter(g => g !== genre)
        : [...prev.genres, genre]
    }));
  };

  const toggleEnergia = (energia) => {
    setCriteria(prev => ({
      ...prev,
      energia: prev.energia.includes(energia)
        ? prev.energia.filter(e => e !== energia)
        : [...prev.energia, energia]
    }));
  };

  const toggleMomento = (momento) => {
    setCriteria(prev => ({
      ...prev,
      momentos: prev.momentos.includes(momento)
        ? prev.momentos.filter(m => m !== momento)
        : [...prev.momentos, momento]
    }));
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-800 rounded-lg w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-6 border-b border-gray-700 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <Sparkles className="text-purple-400" size={28} />
            <h2 className="text-2xl font-bold text-white">Playlists Inteligentes</h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white"
          >
            <X size={24} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {!showCreateDialog ? (
            // Saved playlists list
            <div>
              <div className="flex justify-between items-center mb-6">
                <p className="text-gray-300">
                  Crea playlists dinámicas basadas en criterios como género, BPM, energía y más
                </p>
                <button
                  onClick={() => {
                    resetForm(); // Clear form and editing state
                    setShowCreateDialog(true);
                  }}
                  className="flex items-center gap-2 bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg transition"
                >
                  <Plus size={20} />
                  Nueva Playlist
                </button>
              </div>

              {savedPlaylists.length === 0 ? (
                <div className="text-center py-12 text-gray-400">
                  <Sparkles size={48} className="mx-auto mb-4 opacity-50" />
                  <p>No hay playlists inteligentes guardadas</p>
                  <p className="text-sm mt-2">Crea tu primera playlist para empezar</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {savedPlaylists.map((playlist) => (
                    <div
                      key={playlist.id}
                      className="bg-gray-700 rounded-lg p-4 hover:bg-gray-650 transition"
                    >
                      <div className="flex justify-between items-start mb-2">
                        <h3 className="text-white font-semibold text-lg">{playlist.name}</h3>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(playlist.id);
                          }}
                          className="text-red-400 hover:text-red-300"
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                      
                      <div className="space-y-1 text-sm text-gray-300">
                        {playlist.criteria?.genres?.length > 0 && (
                          <p>📀 Géneros: {playlist.criteria.genres.join(', ')}</p>
                        )}
                        {playlist.criteria && (playlist.criteria.min_bpm || playlist.criteria.max_bpm) && (
                          <p>🎵 BPM: {playlist.criteria.min_bpm || '0'} - {playlist.criteria.max_bpm || '∞'}</p>
                        )}
                        {playlist.criteria?.energia?.length > 0 && (
                          <p>⚡ Energía: {playlist.criteria.energia.join(', ')}</p>
                        )}
                        {playlist.criteria?.favoritos_only && (
                          <p>⭐ Solo favoritos</p>
                        )}
                        {playlist.criteria?.min_rating && (
                          <p>⭐ Rating mínimo: {playlist.criteria.min_rating}</p>
                        )}
                        {playlist.criteria?.limit && (
                          <p className="text-purple-400 mt-2">Límite: {playlist.criteria.limit} canciones</p>
                        )}
                        {!playlist.criteria && (
                          <p className="text-yellow-400 text-xs">⚠️ Playlist con formato antiguo - Recomendamos crear una nueva</p>
                        )}
                      </div>
                      
                      <div className="flex gap-2 mt-4 pt-4 border-t border-gray-600">
                        <button
                          onClick={() => handleLoadPlaylist(playlist)}
                          className="flex-1 flex items-center justify-center gap-2 bg-purple-600 hover:bg-purple-700 text-white px-3 py-2 rounded-lg transition text-sm"
                          data-testid={`edit-smart-playlist-${playlist.id}`}
                        >
                          <Pencil size={16} />
                          Editar
                        </button>
                        {onApplyToCustomList && (
                          <button
                            onClick={() => handleApplyToCustomList(playlist)}
                            className="flex-1 flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 text-white px-3 py-2 rounded-lg transition text-sm"
                          >
                            <Play size={16} />
                            Aplicar a Lista
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : (
            // Create/Edit dialog
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Criteria Form */}
              <div className="space-y-4">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-xl font-semibold text-white flex items-center gap-2">
                    <Filter size={20} />
                    {editingPlaylistId ? 'Editar Playlist' : 'Nueva Playlist'}
                  </h3>
                  <button
                    onClick={() => {
                      setShowCreateDialog(false);
                      resetForm();
                    }}
                    className="text-gray-400 hover:text-white text-sm"
                  >
                    ← Volver
                  </button>
                </div>

                {/* Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Nombre de la Playlist *
                  </label>
                  <input
                    type="text"
                    value={criteria.name}
                    onChange={(e) => setCriteria({ ...criteria, name: e.target.value })}
                    className="w-full bg-gray-700 text-white rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500"
                    placeholder="Ej: Rock Energético"
                  />
                </div>

                {/* Genres */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Géneros
                  </label>
                  <div className="flex flex-wrap gap-2 max-h-32 overflow-y-auto bg-gray-700 p-3 rounded-lg">
                    {genresList.length > 0 ? (
                      genresList.map((genre) => (
                        <button
                          key={genre}
                          onClick={() => toggleGenre(genre)}
                          className={`px-3 py-1 rounded-full text-sm transition ${
                            criteria.genres.includes(genre)
                              ? 'bg-purple-600 text-white'
                              : 'bg-gray-600 text-gray-300 hover:bg-gray-500'
                          }`}
                        >
                          {genre}
                        </button>
                      ))
                    ) : (
                      <p className="text-gray-400 text-sm">No hay géneros disponibles</p>
                    )}
                  </div>
                </div>

                {/* BPM Range */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      BPM Mínimo
                    </label>
                    <input
                      type="number"
                      value={criteria.min_bpm}
                      onChange={(e) => setCriteria({ ...criteria, min_bpm: e.target.value })}
                      className="w-full bg-gray-700 text-white rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500"
                      placeholder="60"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      BPM Máximo
                    </label>
                    <input
                      type="number"
                      value={criteria.max_bpm}
                      onChange={(e) => setCriteria({ ...criteria, max_bpm: e.target.value })}
                      className="w-full bg-gray-700 text-white rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500"
                      placeholder="180"
                    />
                  </div>
                </div>

                {/* Energy */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Energía
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {ENERGIA_OPTIONS.map((energia) => (
                      <button
                        key={energia}
                        onClick={() => toggleEnergia(energia)}
                        className={`px-3 py-1 rounded-full text-sm transition ${
                          criteria.energia.includes(energia)
                            ? 'bg-orange-600 text-white'
                            : 'bg-gray-600 text-gray-300 hover:bg-gray-500'
                        }`}
                      >
                        {energia}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Momentos */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Momentos
                  </label>
                  <div className="flex flex-wrap gap-2 max-h-24 overflow-y-auto">
                    {MOMENTOS_OPTIONS.map((momento) => (
                      <button
                        key={momento}
                        onClick={() => toggleMomento(momento)}
                        className={`px-3 py-1 rounded-full text-sm transition ${
                          criteria.momentos.includes(momento)
                            ? 'bg-blue-600 text-white'
                            : 'bg-gray-600 text-gray-300 hover:bg-gray-500'
                        }`}
                      >
                        {momento}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Other Filters */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Rating Mínimo
                    </label>
                    <select
                      value={criteria.min_rating}
                      onChange={(e) => setCriteria({ ...criteria, min_rating: e.target.value })}
                      className="w-full bg-gray-700 text-white rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500"
                    >
                      <option value="">Cualquiera</option>
                      <option value="1">⭐ 1+</option>
                      <option value="2">⭐⭐ 2+</option>
                      <option value="3">⭐⭐⭐ 3+</option>
                      <option value="4">⭐⭐⭐⭐ 4+</option>
                      <option value="5">⭐⭐⭐⭐⭐ 5</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Límite de Canciones
                    </label>
                    <input
                      type="number"
                      value={criteria.limit}
                      onChange={(e) => setCriteria({ ...criteria, limit: e.target.value })}
                      className="w-full bg-gray-700 text-white rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500"
                      placeholder="50"
                    />
                  </div>
                </div>

                {/* Favoritos Only */}
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="favoritos_only"
                    checked={criteria.favoritos_only}
                    onChange={(e) => setCriteria({ ...criteria, favoritos_only: e.target.checked })}
                    className="w-4 h-4 text-purple-600 bg-gray-700 border-gray-600 rounded focus:ring-purple-500"
                  />
                  <label htmlFor="favoritos_only" className="ml-2 text-sm text-gray-300">
                    Solo canciones favoritas ⭐
                  </label>
                </div>

                {/* Action Buttons */}
                <div className="flex gap-3 pt-4">
                  <button
                    onClick={handleGenerate}
                    disabled={isGenerating || !criteria.name.trim()}
                    className="flex-1 flex items-center justify-center gap-2 bg-purple-600 hover:bg-purple-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white px-4 py-3 rounded-lg transition font-semibold"
                  >
                    <Play size={20} />
                    {isGenerating ? 'Generando...' : 'Generar'}
                  </button>
                  <button
                    onClick={handleSave}
                    disabled={isSaving || !criteria.name.trim()}
                    className="flex-1 flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white px-4 py-3 rounded-lg transition font-semibold"
                    data-testid="save-smart-playlist-btn"
                  >
                    <Save size={20} />
                    {isSaving ? 'Guardando...' : (editingPlaylistId ? 'Actualizar' : 'Guardar')}
                  </button>
                </div>
              </div>

              {/* Results Preview */}
              <div className="bg-gray-700 rounded-lg p-4">
                <h3 className="text-lg font-semibold text-white mb-4">
                  Resultados {generatedSongs.length > 0 && `(${generatedSongs.length} canciones)`}
                </h3>
                
                {generatedSongs.length === 0 ? (
                  <div className="text-center py-12 text-gray-400">
                    <Play size={48} className="mx-auto mb-4 opacity-50" />
                    <p>Haz clic en "Generar" para ver las canciones</p>
                    <p className="text-sm mt-2">que coinciden con tus criterios</p>
                  </div>
                ) : (
                  <div className="space-y-2 max-h-[500px] overflow-y-auto">
                    {generatedSongs.map((song, index) => (
                      <div
                        key={song.id || index}
                        className="bg-gray-800 rounded p-3 hover:bg-gray-750 transition"
                      >
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <p className="text-white font-medium">{song.title}</p>
                            <p className="text-gray-400 text-sm">{song.artist}</p>
                          </div>
                          <div className="text-right text-sm space-y-1">
                            {song.bpm && (
                              <p className="text-purple-400">{song.bpm} BPM</p>
                            )}
                            {song.rating && (
                              <p className="text-yellow-400">{'⭐'.repeat(song.rating)}</p>
                            )}
                          </div>
                        </div>
                        {song.genres && song.genres.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-2">
                            {song.genres.slice(0, 3).map((genre) => (
                              <span
                                key={genre}
                                className="text-xs bg-gray-700 text-gray-300 px-2 py-0.5 rounded"
                              >
                                {genre}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SmartPlaylists;
